import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Slider } from 'primereact/slider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Stage, Layer, Line } from 'react-konva';
import { Divider } from 'primereact/divider';

import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../utils/api';
import { getFallbackAvatar } from '../static/assets/avatars';

import '../static/styles/Passport.css';

const headerContent = {
    header: 'Passport',
    emoji: '🧭',
    subheading: 'Leave a sketch and a note in the travel log — every memory deserves a stamp in this passport.',
};

const COLOR_CHOICES = ['#FF0000', '#0000FF', '#FFFF00', '#008000', '#FFA500', '#800080', '#000000', '#FFFFFF'];
const CANVAS_SIZE = { width: 420, height: 420 };
const PREVIEW_CANVAS = 280;
const PREVIEW_SCALE = PREVIEW_CANVAS / CANVAS_SIZE.width;
const NOTE_COLORS = ['#FFE4A8', '#FFD7E0', '#E1F6FF', '#E3FFD4', '#FBE0FF'];
const CANVAS_BACKGROUND = '#d7d7d9';

const sanitizeAvatar = (value) => {
    if (!value) {
        return undefined;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
        return undefined;
    }
    try {
        const url = new URL(trimmed);
        if (url.hostname === 'example.com') {
            return undefined;
        }
    } catch (error) {
        // non-url, ignore
    }
    return trimmed;
};

const formatDate = (value) => {
    if (!value) {
        return '';
    }

    const resolveDate = (input) => {
        if (input instanceof Date) {
            return input;
        }

        if (typeof input === 'string' || typeof input === 'number') {
            return new Date(input);
        }

        if (input && typeof input === 'object') {
            if (input.$date) {
                return new Date(input.$date);
            }

            if (typeof input.seconds === 'number') {
                return new Date(input.seconds * 1000);
            }
        }

        return new Date(input);
    };

    try {
        const parsed = resolveDate(value);
        if (Number.isNaN(parsed.getTime())) {
            return '';
        }

        return parsed.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch (error) {
        return '';
    }
};

const preparePayloadLines = (lines) => (
    Array.isArray(lines)
        ? lines.map((line) => ({
            tool: line.tool || 'pen',
            color: line.color || '#000000',
            size: line.size || 2,
            points: Array.isArray(line.points) ? line.points : [],
        }))
        : []
);

const renderDrawing = (lines, scale = 1) => (
    <Stage
        width={CANVAS_SIZE.width}
        height={CANVAS_SIZE.height}
        scaleX={scale}
        scaleY={scale}
        className="passport__drawing"
        style={{
            width: CANVAS_SIZE.width * scale,
            height: CANVAS_SIZE.height * scale,
            pointerEvents: 'none',
            background: CANVAS_BACKGROUND,
        }}
    >
        <Layer>
            {(lines || []).map((line, index) => (
                <Line
                    key={index}
                    points={line.points}
                    stroke={line.tool === 'eraser' ? CANVAS_BACKGROUND : line.color}
                    strokeWidth={line.size}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                />
            ))}
        </Layer>
    </Stage>
);

const buildStickyStyle = (key) => {
    if (!key) {
        return {
            '--note-rotation': '0deg',
            '--note-color': NOTE_COLORS[0],
        };
    }

    let hash = 0;
    const value = String(key);
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) | 0;
    }

    const colorIndex = Math.abs(hash) % NOTE_COLORS.length;
    const rotation = ((hash % 8) - 4) * 1.2; // Between roughly -4.8deg and 4.8deg

    return {
        '--note-rotation': `${rotation.toFixed(2)}deg`,
        '--note-color': NOTE_COLORS[colorIndex],
    };
};

export default function Passport() {
    const { user, loading: authLoading, isConfigured, signInWithGoogle, signOut } = useAuth();

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const [comment, setComment] = useState('');
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [lines, setLines] = useState([]);
    const [history, setHistory] = useState([]);
    const [color, setColor] = useState('#FF0000');
    const [size, setSize] = useState(5);
    const [isEraser, setIsEraser] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState('');
    const [isSizePanelOpen, setIsSizePanelOpen] = useState(false);

    const stageRef = useRef(null);
    const isDrawing = useRef(false);
    const sliderControlsRef = useRef(null);

    const isAuthenticated = Boolean(user);

    const resolveAvatar = useCallback((value, key) => sanitizeAvatar(value) || getFallbackAvatar(key), []);

    const userIdentifiers = useMemo(() => {
        if (!user) {
            return [];
        }

        return [user.id, user.providerId, user.email].filter(Boolean);
    }, [user]);

    const isEntryOwner = useCallback((entry) => {
        if (!entry || !userIdentifiers.length) {
            return false;
        }

        const identifiers = [entry.user_id, entry.provider_id, entry.email].filter(Boolean);
        return identifiers.some((value) => userIdentifiers.includes(value));
    }, [userIdentifiers]);

    const loadEntries = useCallback(async ({ signal, showSpinner = true } = {}) => {
        if (showSpinner) {
            setLoading(true);
        }

        try {
            const response = await fetch(buildApiUrl('/passport'), { signal });
            if (!response.ok) {
                throw new Error(`Failed to fetch passport entries: ${response.status}`);
            }

            const payload = await response.json();
            if (Array.isArray(payload)) {
                setEntries(payload.map((entry) => ({
                    ...entry,
                    drawing: Array.isArray(entry.drawing) ? entry.drawing : [],
                })));
            } else {
                setEntries([]);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                // eslint-disable-next-line no-console
                console.error('Error fetching passport entries:', error);
            }
        } finally {
            if (showSpinner) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadEntries({ signal: controller.signal }).finally(() => {});
        return () => controller.abort();
    }, [loadEntries]);

    const triggerSignIn = useCallback(async () => {
        if (!isConfigured) {
            return;
        }

        try {
            await signInWithGoogle();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to sign in with Google', error);

            if (error?.message) {
                window.alert(error.message);
            }
        }
    }, [isConfigured, signInWithGoogle]);

    const triggerSignOut = useCallback(async () => {
        try {
            await signOut();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to sign out', error);
        }
    }, [signOut]);

    const resetBoard = () => {
        setLines([]);
        setHistory([]);
        setComment('');
        setColor('#FF0000');
        setSize(5);
        setIsEraser(false);
        setSubmissionError('');
        setIsSizePanelOpen(false);
    };

    const handleOpenBoard = async () => {
        if (!isConfigured) {
            return;
        }

        if (!isAuthenticated) {
            await triggerSignIn();
            return;
        }

        resetBoard();
        setIsDialogVisible(true);
    };

    const handleDialogHide = () => {
        if (isSubmitting) {
            return;
        }

        setIsSizePanelOpen(false);
        setIsDialogVisible(false);
    };

    const handleMouseDown = () => {
        if (!isAuthenticated) {
            return;
        }

        isDrawing.current = true;
        const stage = stageRef.current;
        if (!stage) {
            return;
        }
        const pos = stage.getPointerPosition();
        if (!pos) {
            return;
        }

        setLines((prev) => (
            [...prev, {
                tool: isEraser ? 'eraser' : 'pen',
                color: isEraser ? CANVAS_BACKGROUND : color,
                size,
                points: [pos.x, pos.y],
            }]
        ));
    };

    const handleMouseMove = () => {
        if (!isDrawing.current) {
            return;
        }
        const stage = stageRef.current;
        if (!stage) {
            return;
        }

        const point = stage.getPointerPosition();
        if (!point) {
            return;
        }

        setLines((prev) => {
            if (!prev.length) {
                return prev;
            }

            const next = [...prev];
            const lastLine = { ...next[next.length - 1] };
            lastLine.points = [...lastLine.points, point.x, point.y];
            next[next.length - 1] = lastLine;
            return next;
        });
    };

    const endDrawing = () => {
        if (!isDrawing.current) {
            return;
        }
        isDrawing.current = false;
        setHistory([]);
    };

    const handleUndo = () => {
        setLines((prev) => {
            if (!prev.length) {
                return prev;
            }
            const next = [...prev];
            const removed = next.pop();
            setHistory((prevHistory) => (removed ? [...prevHistory, removed] : prevHistory));
            return next;
        });
    };

    const handleRedo = () => {
        setHistory((prevHistory) => {
            if (!prevHistory.length) {
                return prevHistory;
            }
            const nextHistory = [...prevHistory];
            const restored = nextHistory.pop();
            if (restored) {
                setLines((prevLines) => [...prevLines, restored]);
            }
            return nextHistory;
        });
    };

    const handleClear = () => {
        resetBoard();
    };

    useEffect(() => {
        if (!isSizePanelOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (sliderControlsRef.current && sliderControlsRef.current.contains(event.target)) {
                return;
            }
            setIsSizePanelOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [isSizePanelOpen]);

    const handleSubmit = async () => {
        if (!isAuthenticated || !user?.accessToken) {
            setSubmissionError('Please sign in to post your entry.');
            return;
        }

        const trimmedComment = comment.trim();
        if (!trimmedComment && !lines.length) {
            setSubmissionError('Add a drawing or write a message before submitting.');
            return;
        }

        setSubmissionError('');
        setIsSubmitting(true);

        try {
            const response = await fetch(buildApiUrl('/passport/create'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.accessToken}`,
                },
                body: JSON.stringify({
                    comment: trimmedComment,
                    drawing: preparePayloadLines(lines),
                }),
            });

            if (!response.ok) {
                const message = await response.json().catch(() => ({}));
                throw new Error(message?.detail || message?.message || 'Failed to save entry.');
            }

            await loadEntries({ showSpinner: false });
            resetBoard();
            setIsDialogVisible(false);
        } catch (error) {
            setSubmissionError(error?.message || 'Failed to save entry.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteEntry = useCallback(async (entryId) => {
        if (!user?.accessToken) {
            throw new Error('Please sign in to manage passport entries.');
        }

        const response = await fetch(buildApiUrl(`/passport/${entryId}`), {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${user.accessToken}`,
            },
        });

        if (!response.ok) {
            const message = await response.json().catch(() => ({}));
            throw new Error(message?.detail || message?.message || 'Failed to delete entry.');
        }

        await loadEntries({ showSpinner: false });
    }, [user?.accessToken, loadEntries]);

    return (
        <div className="passport">
            <PageHeader data={headerContent} />

            <div className="flex flex-column gap-3 my-4">
                <div className="flex flex-column lg:flex-row align-items-center justify-content-between gap-2">
                    <Button
                        label="Open Drawing Board"
                        icon="pi pi-pencil"
                        onClick={handleOpenBoard}
                        rounded
                        disabled={!isConfigured || authLoading}
                        className="w-full lg:w-auto"
                        size='small'
                    />
                    <div className="flex align-items-center gap-3">
                        {isAuthenticated ? (
                            <>
                                <Avatar
                                    image={resolveAvatar(user?.avatar, user?.id || user?.email)}
                                    shape="circle"
                                    size="large"
                                    label={user?.name?.[0]}
                                />
                                <div className="flex flex-column">
                                    {/* <span className="font-bold">{user?.name || user?.email}</span> */}
                                    <Button
                                        label="Sign out"
                                        icon="pi pi-sign-out"
                                        onClick={triggerSignOut}
                                        outlined
                                        rounded
                                        size='small'
                                    />
                                </div>
                            </>
                        ) : (
                            <Button
                                label="Sign in with Google"
                                icon="pi pi-google"
                                onClick={triggerSignIn}
                                loading={authLoading}
                                disabled={!isConfigured || authLoading}
                                outlined
                                rounded
                                size='small'
                            />
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                header="Add your drawing"
                visible={isDialogVisible}
                className="passport__board-dialog"
                contentClassName="passport__board-dialog-content"
                style={{ width: '34rem', maxWidth: '95vw' }}
                breakpoints={{ '960px': '90vw', '640px': '96vw' }}
                onHide={handleDialogHide}
                footer={(
                    <div className="passport__board-footer">
                        <div className="passport__board-counter" aria-live="polite">
                            <span>{Math.max(0, 50 - comment.length)}</span>
                        </div>
                        <div className="passport__board-actions">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={handleDialogHide}
                                text
                                disabled={isSubmitting}
                            />
                            <Button
                                label="Submit"
                                icon="pi pi-check"
                                onClick={handleSubmit}
                                loading={isSubmitting}
                                disabled={!isAuthenticated}
                                outlined
                            />
                        </div>
                    </div>
                )}
            >
                {!isAuthenticated && (
                    <p className="p-error">Sign in to share your artwork.</p>
                )}

                <div className="passport__board-layout">
                    <div className="passport__board-toolbar">
                        <div className="passport__board-swatches">
                            {COLOR_CHOICES.map((swatch) => (
                                <Button
                                    key={swatch}
                                    rounded
                                    size="small"
                                    className={`passport__board-swatch${!isEraser && color === swatch ? ' passport__board-swatch--active' : ''}`}
                                    style={{ backgroundColor: swatch, borderColor: swatch === '#FFFFFF' ? '#e2e8f0' : swatch }}
                                    onClick={() => {
                                        setIsEraser(false);
                                        setColor(swatch);
                                    }}
                                    disabled={!isAuthenticated}
                                />
                            ))}
                        </div>

                        <div className="passport__board-toolset">
                            <div className="passport__board-tools">
                                <Button
                                    rounded
                                    size="small"
                                    className="passport__board-tool"
                                    icon="pi pi-undo"
                                    onClick={handleUndo}
                                    disabled={!isAuthenticated || !lines.length}
                                />
                                <Button
                                    rounded
                                    size="small"
                                    className="passport__board-tool"
                                    icon="pi pi-refresh"
                                    onClick={handleRedo}
                                    disabled={!isAuthenticated || !history.length}
                                />

                                <Button
                                    rounded
                                    size="small"
                                    className={`passport__board-tool${!isEraser ? ' passport__board-tool--active' : ''}`}
                                    icon="pi pi-pencil"
                                    onClick={() => setIsEraser(false)}
                                    disabled={!isAuthenticated}
                                />
                                <Button
                                    rounded
                                    size="small"
                                    className={`passport__board-tool${isEraser ? ' passport__board-tool--active' : ''}`}
                                    icon="pi pi-eraser"
                                    onClick={() => setIsEraser(true)}
                                    disabled={!isAuthenticated}
                                />
                                <Button
                                    rounded
                                    size="small"
                                    className="passport__board-tool"
                                    icon="pi pi-trash"
                                    onClick={handleClear}
                                    disabled={!isAuthenticated || (!lines.length && !history.length && !comment)}
                                />
                                <div className="passport__board-tool-wrapper passport__board-tool--size" ref={sliderControlsRef}>
                                    <Button
                                        rounded
                                        size="small"
                                        className={`passport__board-tool-toggle${isSizePanelOpen ? ' passport__board-tool--active' : ''}`}
                                        icon="pi pi-sliders-h"
                                        onClick={() => setIsSizePanelOpen((previous) => !previous)}
                                        disabled={!isAuthenticated}
                                    />
                                    {isSizePanelOpen && (
                                        <div className="passport__board-slider-panel">
                                            <Slider
                                                value={size}
                                                onChange={(e) => setSize(e.value)}
                                                className="passport__board-slider-control"
                                                min={1}
                                                max={20}
                                                disabled={!isAuthenticated}
                                            />
                                            <div className="passport__board-size-preview">
                                                <span style={{ width: `${Math.max(4, size)}px`, height: `${Math.max(4, size)}px` }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="passport__board-stage-wrapper">
                        <Stage
                            ref={stageRef}
                            width={CANVAS_SIZE.width}
                            height={CANVAS_SIZE.height}
                            className="passport__board-stage"
                            style={{ background: CANVAS_BACKGROUND, border: '1px solid #2a2a2f' }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={endDrawing}
                            onTouchStart={handleMouseDown}
                            onTouchMove={handleMouseMove}
                            onTouchEnd={endDrawing}
                        >
                            <Layer>
                                {lines.map((line, index) => (
                                    <Line
                                        key={index}
                                        points={line.points}
                stroke={line.tool === 'eraser' ? CANVAS_BACKGROUND : line.color}
                                        strokeWidth={line.size}
                                        tension={0.5}
                                        lineCap="round"
                                        lineJoin="round"
                                    />
                                ))}
                            </Layer>
                        </Stage>
                    </div>

                    <div className="passport__board-bottom">
                        <div className="passport__board-message">
                            <InputTextarea
                                value={comment}
                                onChange={(e) => {
                                    const next = e.target.value.slice(0, 50);
                                    setComment(next);
                                }}
                                rows={1}
                                cols={30}
                                placeholder="Type your message here..."
                                disabled={!isAuthenticated}
                                autoResize
                                className="passport-message-input"
                                maxLength={50}
                            />
                        </div>
                    </div>
                </div>

                {submissionError && <p className="p-error mt-3">{submissionError}</p>}
            </Dialog>

            {loading ? (
                <div className="flex justify-content-center my-6">
                    <ProgressSpinner style={{ width: '4rem', height: '4rem' }} strokeWidth="5" />
                </div>
            ) : (
                <>
                    <Divider />
                    <div className="passport__entries grid">
                    {entries.length > 0 ? (
                        entries.map((entry) => {
                            const stickyStyle = buildStickyStyle(entry._id || entry.created_at);
                            const canDeleteEntry = isEntryOwner(entry);

                            return (
                                <div key={entry._id} className="col-12 sm:col-6 lg:col-5">
                                    <div className="passport__entry" style={stickyStyle}>
                                        <span className="flex align-items-center gap-2">
                                            <Avatar
                                                image={resolveAvatar(entry.avatar, entry.provider_id || entry.email || entry._id)}
                                                icon={!resolveAvatar(entry.avatar, entry.provider_id || entry.email || entry._id) ? 'pi pi-user' : undefined}
                                                shape="circle"
                                                label={!resolveAvatar(entry.avatar, entry.provider_id || entry.email || entry._id) ? entry.author?.[0] : undefined}
                                            />
                                            <span className="flex flex-column">
                                                <span className="font-bold">{entry.author}</span>
                                                <span className="comment-date">{formatDate(entry.created_at)}</span>
                                            </span>
                                            {canDeleteEntry && (
                                                <Button
                                                    icon="pi pi-trash"
                                                    text
                                                    rounded
                                                    severity="danger"
                                                    className="ml-auto"
                                                    onClick={() => {
                                                        handleDeleteEntry(entry._id).catch((error) => {
                                                            // eslint-disable-next-line no-console
                                                            console.error('Failed to delete entry:', error);
                                                        });
                                                    }}
                                                    aria-label="Delete entry"
                                                />
                                            )}
                                        </span>
                                        {entry.drawing?.length > 0 && (
                                            <div className="passport__drawing-wrapper">
                                                {renderDrawing(entry.drawing, PREVIEW_SCALE)}
                                            </div>
                                        )}
                                        {entry.comment && <p className="passport__comment">{entry.comment}</p>}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12">
                            <p className="text-center">No entries yet. Be the first to leave a drawing!</p>
                        </div>
                    )}
                </div>
                </>
            )}

            <Footer />
        </div>
    );
}
